import React, { useEffect, useMemo, useState } from "react";
import { kcFetch, API_BASE } from "../../lib/apiClient";
import { useAppSettings, AppLogo } from "../../lib/AppSettingsContext";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// PublicAppointmentBookingPage — multi-step booking flow:
//   1. Pick a pastor (skipped if directory has only one)
//   2. Pick appointment type (Short / Long card)
//   3. Pick available day chip
//   4. Pick available time slot (computed by backend)
//   5. Fill details (name/email/phone/communication preference/subject/reason)
//   6. Optional emergency-crisis toggle (notifies pastor urgently)
// On submit -> branded confirmation screen.
// ============================================================
const DAY_LABEL = { sun: "SUN", mon: "MON", tue: "TUE", wed: "WED", thu: "THU", fri: "FRI", sat: "SAT" };
const DAY_FULL = { sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday" };
const COMM_OPTIONS = [
  { value: "in-person", label: "In-person" },
  { value: "phone",     label: "Phone Call" },
  { value: "video",     label: "Video Call" },
];

export default function PublicAppointmentBookingPage() {
  const { settings } = useAppSettings();
  const apptShortName = settings.apptShortName || "On-the-Fly Connections";
  const apptShortDuration = settings.apptShortDuration ?? 20;
  const apptLongName = settings.apptLongName || "Weekday Connections";
  const apptLongDuration = settings.apptLongDuration ?? 60;
  const emergencyPhone = settings.emergencyPhone || "";
  const churchPhone = settings.churchPhone || "";

  const [pastors, setPastors] = useState([]);
  const [loadingPastors, setLoadingPastors] = useState(true);
  const [selectedPastorId, setSelectedPastorId] = useState("");

  const [apptType, setApptType] = useState(""); // "short" | "long"
  const [pickedDay, setPickedDay] = useState(""); // mon..sun
  const [pickedSlot, setPickedSlot] = useState("");
  const [slots, setSlots] = useState({ date: null, slots: [], durationMinutes: 0 });
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [form, setForm] = useState({
    visitorName: "", visitorEmail: "", visitorPhone: "",
    communicationPref: "", subject: "", reason: "",
    isCrisis: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");

  // Load pastor directory
  useEffect(() => {
    (async () => {
      try {
        const r = await kcFetch(`${API_BASE}/public/pastors`);
        const data = await r.json();
        const list = Array.isArray(data) ? data : [];
        setPastors(list);
        if (list.length === 1) setSelectedPastorId(list[0].id);
      } catch (e) { /* swallow */ }
      finally { setLoadingPastors(false); }
    })();
  }, []);

  const selectedPastor = pastors.find(p => p.id === selectedPastorId) || null;

  // Reset downstream state when pastor or type changes
  useEffect(() => {
    setApptType(""); setPickedDay(""); setPickedSlot("");
    setSlots({ date: null, slots: [], durationMinutes: 0 });
  }, [selectedPastorId]);
  useEffect(() => {
    setPickedDay(""); setPickedSlot("");
    setSlots({ date: null, slots: [], durationMinutes: 0 });
    // Subject default = appointment-type name
    if (apptType === "short") setForm(f => ({ ...f, subject: apptShortName }));
    else if (apptType === "long") setForm(f => ({ ...f, subject: apptLongName }));
  }, [apptType, apptShortName, apptLongName]);

  // Resolve availability days for the picked type
  const availability = useMemo(() => {
    if (!selectedPastor || !apptType) return { days: [], start: "", end: "" };
    return apptType === "short"
      ? (selectedPastor.apptShortAvailability || { days: [], start: "", end: "" })
      : (selectedPastor.apptLongAvailability  || { days: [], start: "", end: "" });
  }, [selectedPastor, apptType]);
  const availableDays = (availability && availability.days) || [];

  // Fetch slots when day changes
  useEffect(() => {
    if (!selectedPastorId || !apptType || !pickedDay) return;
    setSlotsLoading(true); setPickedSlot("");
    (async () => {
      try {
        const r = await kcFetch(`${API_BASE}/public/pastors/${selectedPastorId}/slots?day=${pickedDay}&appt_type=${apptType}`);
        const j = await r.json();
        setSlots({
          date: j.date || null,
          slots: Array.isArray(j.slots) ? j.slots : [],
          durationMinutes: j.durationMinutes || 0,
        });
      } catch (e) {
        setSlots({ date: null, slots: [], durationMinutes: 0 });
      } finally { setSlotsLoading(false); }
    })();
  }, [selectedPastorId, apptType, pickedDay]);

  const updateForm = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedPastor) { setError("Please pick a pastor."); return; }
    if (selectedPastor.isAway) { setError(`${selectedPastor.name} is currently away.`); return; }
    if (!apptType) { setError("Please pick an appointment type."); return; }
    if (!pickedDay) { setError("Please pick an available day."); return; }
    if (!pickedSlot) { setError("Please pick an available time."); return; }
    if (!form.visitorName.trim()) { setError("Please enter your full name."); return; }
    if (!form.communicationPref) { setError("Please pick a communication preference."); return; }
    if (!form.visitorEmail.trim() && !form.visitorPhone.trim()) { setError("Please share an email or phone so we can confirm."); return; }
    setSubmitting(true);
    try {
      const r = await kcFetch(`${API_BASE}/public/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pastorId: selectedPastor.id,
          visitorName: form.visitorName.trim(),
          visitorEmail: form.visitorEmail.trim(),
          visitorPhone: form.visitorPhone.trim(),
          title: (form.subject || (apptType === "short" ? apptShortName : apptLongName)).trim(),
          reason: form.reason.trim(),
          date: slots.date,
          time: pickedSlot,
          apptType,
          communicationPref: form.communicationPref,
          isCrisis: !!form.isCrisis,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not submit");
      }
      const j = await r.json();
      setConfirmation({
        id: j.id,
        pastor: selectedPastor,
        apptType, apptName: apptType === "short" ? apptShortName : apptLongName,
        day: pickedDay, date: slots.date, time: pickedSlot,
        form: { ...form },
      });
    } catch (err) {
      setError(err.message || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (confirmation) {
    return <ConfirmationScreen confirmation={confirmation} appSettings={settings} />;
  }

  return (
    <div className="public-book-page" data-testid="public-booking-page">
      <style>{CSS}</style>
      <div className="public-book-topbar">
        <AppLogo size={56} className="public-book-logo" style={{ borderRadius: 10 }} />
        <div className="public-book-brand">
          <div className="pbb-title">{settings.appName || "Kingdom Connect"}</div>
          <div className="pbb-sub">{settings.appTagline || "To Communicate the Love of Christ"}</div>
        </div>
      </div>

      <div className="public-book-wrap">
        <header className="public-book-hero">
          <h1>Book a Pastoral Appointment</h1>
          <p>Pick a pastor, choose an appointment type and a time that works for you.</p>
        </header>

        {/* Step 1 — pick a pastor */}
        <section className="public-book-section" data-testid="step-pastor">
          <h2>1. Choose a Pastor</h2>
          {loadingPastors ? (
            <div className="public-book-empty">Loading pastors…</div>
          ) : pastors.length === 0 ? (
            <div className="public-book-empty">No pastors are available right now.</div>
          ) : (
            <div className="pastor-grid" data-testid="pastor-grid">
              {pastors.map(p => (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  className={`pastor-card ${selectedPastorId === p.id ? "is-selected" : ""}`}
                  data-testid={`pastor-card-${p.id}`}
                  onClick={() => setSelectedPastorId(p.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedPastorId(p.id); } }}
                >
                  <div className="pastor-card-photo" aria-hidden="true">
                    {p.photo ? <img src={p.photo} alt="" /> : <span>{(p.name || "?").charAt(0)}</span>}
                  </div>
                  <div className="pastor-card-meta">
                    <div className="pastor-card-name">{p.name}</div>
                    {p.title && <div className="pastor-card-title">{p.title}</div>}
                    {p.isAway && <div className="pastor-card-away" data-testid={`pastor-card-away-${p.id}`}>AWAY</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Away message replaces the rest of the flow */}
        {selectedPastor && selectedPastor.isAway && (
          <section className="public-book-section public-book-away" data-testid="pastor-away-message">
            <h2>{selectedPastor.name} is away</h2>
            <p>{selectedPastor.awayMessage || `${selectedPastor.name} is currently away. Please contact the main church line.`}</p>
            {(selectedPastor.awayPhone || churchPhone) && (
              <p>
                <a className="public-book-tel-link" data-testid="pastor-away-phone-link" href={`tel:${selectedPastor.awayPhone || churchPhone}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <CustomIcon name="phone" size={14} /> {selectedPastor.awayPhone || churchPhone}
                </a>
              </p>
            )}
          </section>
        )}

        {/* Step 2 — pick appointment type */}
        {selectedPastor && !selectedPastor.isAway && (
          <section className="public-book-section" data-testid="step-type">
            <h2>2. Pick Appointment Type</h2>
            <div className="appt-type-grid">
              <div
                role="button"
                tabIndex={0}
                className={`appt-type-card ${apptType === "short" ? "is-selected" : ""}`}
                data-testid="appt-type-short"
                onClick={() => setApptType("short")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setApptType("short"); } }}
              >
                <div className="atc-title">{apptShortName}</div>
                <div className="atc-dur">{apptShortDuration} min max</div>
                <div className="atc-desc">Brief connection after service</div>
              </div>
              <div
                role="button"
                tabIndex={0}
                className={`appt-type-card ${apptType === "long" ? "is-selected" : ""}`}
                data-testid="appt-type-long"
                onClick={() => setApptType("long")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setApptType("long"); } }}
              >
                <div className="atc-title">{apptLongName}</div>
                <div className="atc-dur">{apptLongDuration} min max</div>
                <div className="atc-desc">In-depth weekday meeting</div>
              </div>
            </div>
          </section>
        )}

        {/* Step 3 — pick day */}
        {selectedPastor && !selectedPastor.isAway && apptType && (
          <section className="public-book-section" data-testid="step-day">
            <h2>3. Pick Available Day</h2>
            {availableDays.length === 0 ? (
              <div className="public-book-empty" data-testid="no-days-empty">
                {selectedPastor.name} has not published any availability for {apptType === "short" ? apptShortName : apptLongName} yet.
              </div>
            ) : (
              <div className="day-chip-row" data-testid="day-chip-row">
                {availableDays.map(d => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`day-chip-${d}`}
                    onClick={() => setPickedDay(d)}
                    className={`day-chip ${pickedDay === d ? "is-selected" : ""}`}
                  >
                    {DAY_LABEL[d]}
                    <span className="day-chip-full">{DAY_FULL[d]}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Step 4 — pick slot */}
        {selectedPastor && !selectedPastor.isAway && apptType && pickedDay && (
          <section className="public-book-section" data-testid="step-slot">
            <h2>4. Pick Available Time</h2>
            {slotsLoading ? (
              <div className="public-book-empty">Loading slots…</div>
            ) : slots.slots.length === 0 ? (
              <div className="public-book-empty" data-testid="no-slots-empty">No available slots for that day. Please pick another day.</div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: "var(--kc-sub)", marginBottom: 6 }}>
                  Next {DAY_FULL[pickedDay]}: <strong>{slots.date}</strong> · {slots.durationMinutes}-minute slots
                </div>
                <select data-testid="slot-select" value={pickedSlot} onChange={e => setPickedSlot(e.target.value)}>
                  <option value="">Choose a time…</option>
                  {slots.slots.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
          </section>
        )}

        {/* Step 5 — your details */}
        {selectedPastor && !selectedPastor.isAway && apptType && pickedDay && pickedSlot && (
          <section className="public-book-section" data-testid="step-details">
            <h2>5. Your Details</h2>
            <form className="public-book-form" noValidate onSubmit={submit}>
              <Field label="Full Name *">
                <input data-testid="pb-name" value={form.visitorName} onChange={updateForm("visitorName")} placeholder="Your full name" autoComplete="name" />
              </Field>
              <div className="public-book-row">
                <Field label="Email">
                  <input data-testid="pb-email" type="email" value={form.visitorEmail} onChange={updateForm("visitorEmail")} placeholder="you@example.com" autoComplete="email" />
                </Field>
                <Field label="Phone">
                  <input data-testid="pb-phone" value={form.visitorPhone} onChange={updateForm("visitorPhone")} placeholder="+233 ..." autoComplete="tel" />
                </Field>
              </div>
              <Field label="Communication Preference *">
                <select data-testid="pb-comm-pref" value={form.communicationPref} onChange={updateForm("communicationPref")}>
                  <option value="">Choose…</option>
                  {COMM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Subject">
                <input data-testid="pb-subject" value={form.subject} onChange={updateForm("subject")} placeholder="Subject" />
              </Field>
              <Field label="Reason / Notes (optional)">
                <textarea data-testid="pb-reason" rows={3} value={form.reason} onChange={updateForm("reason")} placeholder="What would you like to discuss?" />
              </Field>

              {/* Step 6 — crisis */}
              <CrisisBlock
                isCrisis={form.isCrisis}
                onToggle={(v) => setForm(f => ({ ...f, isCrisis: v }))}
                emergencyPhone={emergencyPhone}
              />

              {error && <div className="public-book-error" data-testid="public-booking-error">{error}</div>}

              <button type="submit" disabled={submitting} className="public-book-submit" data-testid="public-booking-submit">
                {submitting ? "Submitting…" : "REQUEST APPOINTMENT"}
              </button>
            </form>
          </section>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="public-book-field">
      <span className="pbf-label">{label}</span>
      {children}
    </label>
  );
}

function CrisisBlock({ isCrisis, onToggle, emergencyPhone }) {
  return (
    <div className={`crisis-card ${isCrisis ? "is-active" : ""}`} data-testid="crisis-card">
      <div className="crisis-card-title">NEED IMMEDIATE SUPPORT?</div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", color: isCrisis ? "#ffb4b4" : "rgba(255,255,255,0.8)" }}>
        <input type="checkbox" data-testid="crisis-checkbox" checked={isCrisis} onChange={e => onToggle(e.target.checked)} />
        This is an urgent crisis situation.
      </label>
      {isCrisis && (
        <div data-testid="crisis-details" style={{ marginTop: 10 }}>
          {emergencyPhone ? (
            <a className="crisis-phone" data-testid="crisis-phone-link" href={`tel:${emergencyPhone}`} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><CustomIcon name="phone" size={16} /> {emergencyPhone}</a>
          ) : (
            <div style={{ color: "var(--kc-sub)", fontSize: 12 }}>(No on-call number configured yet — contact the church directly.)</div>
          )}
          <p style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 10, lineHeight: 1.5 }}>
            If this is an immediate medical or physical safety emergency, please call <strong>911</strong> or your local emergency services right away.
          </p>
        </div>
      )}
    </div>
  );
}

function ConfirmationScreen({ confirmation, appSettings }) {
  const c = confirmation;
  return (
    <div className="public-book-page" data-testid="public-booking-success">
      <style>{CSS}</style>
      <div className="public-book-topbar">
        <AppLogo size={56} className="public-book-logo" style={{ borderRadius: 10 }} />
        <div className="public-book-brand">
          <div className="pbb-title">{appSettings.appName || "Kingdom Connect"}</div>
          <div className="pbb-sub">{appSettings.appTagline || "To Communicate the Love of Christ"}</div>
        </div>
      </div>
      <div className="public-book-wrap">
        <div className="public-success-card">
          <div className="public-success-ring" aria-hidden="true"><CustomIcon name="check" size={40} /></div>
          <h1>Request Received</h1>
          <div className="confirm-pastor">
            {c.pastor.photo
              ? <img src={c.pastor.photo} alt="" className="confirm-pastor-photo" />
              : <div className="confirm-pastor-photo confirm-pastor-photo--initials">{(c.pastor.name || "?").charAt(0)}</div>}
            <div>
              <div className="confirm-pastor-name">{c.pastor.name}</div>
              {c.pastor.title && <div className="confirm-pastor-title">{c.pastor.title}</div>}
            </div>
          </div>
          <div className="confirm-grid">
            <div><span className="cg-l">Appointment</span><span className="cg-v">{c.apptName}</span></div>
            <div><span className="cg-l">When</span><span className="cg-v">{DAY_FULL[c.day]} · {c.date} · {c.time}</span></div>
            <div><span className="cg-l">For</span><span className="cg-v">{c.form.visitorName}</span></div>
            <div><span className="cg-l">Preferred</span><span className="cg-v">{(COMM_OPTIONS.find(o => o.value === c.form.communicationPref) || {}).label || c.form.communicationPref}</span></div>
            {c.form.subject && <div><span className="cg-l">Subject</span><span className="cg-v">{c.form.subject}</span></div>}
            {c.form.reason && <div><span className="cg-l">Notes</span><span className="cg-v">{c.form.reason}</span></div>}
            {c.form.isCrisis && <div><span className="cg-l" style={{ color: "#ff9b9b" }}>Status</span><span className="cg-v" style={{ color: "#ff9b9b", display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="warning" size={13} /> Crisis request</span></div>}
          </div>
          <p style={{ marginTop: 16 }}>Your request has been received. <strong>{c.pastor.name}</strong> or their team will confirm your appointment shortly.</p>
          <a href="/" className="public-success-btn">Return Home</a>
        </div>
      </div>
    </div>
  );
}

// CSS exported as template literal for inline mounting.
const CSS = `
  .public-book-page { min-height: 100vh; background: var(--kc-surface); color: var(--kc-text); font-family: 'Montserrat', sans-serif; padding: 24px 16px 80px; }
  .public-book-topbar { max-width: 920px; margin: 0 auto 24px; display: flex; align-items: center; gap: 14px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); padding-bottom: 16px; }
  .public-book-logo { width: 56px; border-radius: 10px; }
  .public-book-brand .pbb-title { font-family: 'Cinzel', serif; font-size: 18px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--kc-text); }
  .public-book-brand .pbb-sub { font-size: 11px; color: var(--kc-accent); font-style: italic; letter-spacing: 0.08em; text-transform: uppercase; margin-top: 4px; }
  .public-book-wrap { max-width: 920px; margin: 0 auto; display: flex; flex-direction: column; gap: 22px; }
  .public-book-hero h1 { font-family: 'Cinzel', serif; font-size: 26px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-text); margin: 0 0 8px; }
  .public-book-hero p { color: var(--kc-sub); font-size: 14px; line-height: 1.55; margin: 0; }
  .public-book-section { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); border-radius: 16px; padding: 22px; }
  .public-book-section h2 { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-accent); margin: 0 0 16px; }
  .public-book-empty { color: var(--kc-sub); font-size: 13px; padding: 14px; text-align: center; }

  .pastor-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
  @media (min-width: 640px) { .pastor-grid { grid-template-columns: repeat(2, 1fr); } }
  .pastor-card { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); border-radius: 14px; cursor: pointer; color: inherit; text-align: left; transition: all 0.15s; min-width: 0; }
  .pastor-card:hover { border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); }
  .pastor-card.is-selected { border-color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .pastor-card-photo { width: 48px; height: 48px; border-radius: 50%; background: color-mix(in srgb, var(--kc-accent) 18%, transparent); display: inline-flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; font-weight: 700; color: var(--kc-accent); }
  .pastor-card-photo img { width: 100%; height: 100%; object-fit: cover; }
  .pastor-card-name { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-text); }
  .pastor-card-title { font-size: 11px; color: var(--kc-sub); }
  .pastor-card-away { display: inline-block; margin-top: 4px; font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 999px; background: color-mix(in srgb, var(--kc-accent) 18%, transparent); color: var(--kc-accent); letter-spacing: 0.08em; }

  .public-book-away h2 { color: var(--kc-accent); }
  .public-book-away p { color: var(--kc-sub); font-size: 14px; line-height: 1.6; }
  .public-book-tel-link { display: inline-block; color: var(--kc-accent); font-size: 18px; font-weight: 700; text-decoration: none; padding: 8px 14px; border-radius: 10px; background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 40%, transparent); margin-top: 8px; }

  .appt-type-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
  @media (min-width: 540px) { .appt-type-grid { grid-template-columns: 1fr 1fr; } }
  .appt-type-card { background: var(--kc-surface); border: 1.5px solid rgba(255,255,255,0.12); border-radius: 14px; padding: 18px 16px; cursor: pointer; color: inherit; text-align: left; transition: all 0.15s; min-width: 0; }
  .appt-type-card:hover { border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); }
  .appt-type-card.is-selected { border-color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .atc-title { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-text); }
  .atc-dur { font-size: 18px; font-weight: 700; color: var(--kc-accent); margin: 6px 0 8px; }
  .atc-desc { font-size: 12px; color: var(--kc-sub); }

  .day-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
  .day-chip { background: transparent; border: 1.5px solid rgba(255,255,255,0.20); color: var(--kc-text); padding: 10px 16px; border-radius: 999px; font-weight: 700; letter-spacing: 0.08em; cursor: pointer; min-width: 80px; display: flex; flex-direction: column; align-items: center; gap: 2px; transition: all 0.15s; }
  .day-chip:hover { border-color: color-mix(in srgb, var(--kc-accent) 40%, transparent); }
  .day-chip.is-selected { background: var(--kc-accent); color: #1A1A1A; border-color: var(--kc-accent); }
  .day-chip-full { font-size: 10px; font-weight: 600; letter-spacing: 0.04em; opacity: 0.85; }

  .public-book-form { display: flex; flex-direction: column; gap: 14px; }
  .public-book-row { display: grid; gap: 14px; grid-template-columns: 1fr; }
  @media (min-width: 640px) { .public-book-row { grid-template-columns: 1fr 1fr; } }
  .public-book-field { display: flex; flex-direction: column; gap: 6px; }
  .pbf-label { font-size: 11px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-sub); }
  .public-book-section input, .public-book-section textarea, .public-book-section select { background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text); border-radius: 10px; padding: 11px 12px; font-family: 'Montserrat', sans-serif; font-size: 14px; outline: none; }
  .public-book-section input:focus, .public-book-section textarea:focus, .public-book-section select:focus { border-color: var(--kc-accent); }
  .public-book-submit { background: var(--kc-accent); color: #1A1A1A; border: none; border-radius: 12px; padding: 14px 18px; cursor: pointer; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; font-size: 13px; }
  .public-book-submit:disabled { opacity: 0.6; cursor: not-allowed; }
  .public-book-error { background: rgba(255,107,107,0.10); border: 1px solid rgba(255,107,107,0.45); color: #ff9b9b; border-radius: 10px; padding: 10px 12px; font-size: 13px; }

  .crisis-card { background: rgba(255,107,107,0.06); border: 2px solid rgba(255,107,107,0.55); border-radius: 14px; padding: 14px 16px; margin-top: 6px; }
  .crisis-card.is-active { background: rgba(255,107,107,0.10); }
  .crisis-card-title { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: #ff6b6b; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; }
  .crisis-phone { font-size: 24px; font-weight: 700; color: var(--kc-accent); text-decoration: none; display: inline-block; }
  .crisis-phone:hover { text-decoration: underline; }

  .public-success-card { max-width: 600px; margin: 24px auto; background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 16px; padding: 32px 28px; text-align: center; }
  .public-success-ring { width: 72px; height: 72px; border-radius: 50%; background: color-mix(in srgb, var(--kc-accent) 12%, transparent); border: 2px solid var(--kc-accent); color: var(--kc-accent); font-size: 34px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 16px; }
  .public-success-card h1 { font-family: 'Cinzel', serif; font-size: 24px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--kc-text); margin: 0 0 14px; }
  .confirm-pastor { display: flex; align-items: center; gap: 12px; justify-content: center; margin: 16px 0 18px; }
  .confirm-pastor-photo { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; background: color-mix(in srgb, var(--kc-accent) 18%, transparent); display: inline-flex; align-items: center; justify-content: center; font-weight: 700; color: var(--kc-accent); }
  .confirm-pastor-photo--initials { font-size: 22px; }
  .confirm-pastor-name { font-family: 'Cinzel', serif; font-size: 16px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em; text-align: left; }
  .confirm-pastor-title { font-size: 11px; color: var(--kc-sub); text-align: left; }
  .confirm-grid { display: grid; gap: 8px; text-align: left; margin: 8px 0; padding: 14px; background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); border-radius: 12px; font-size: 13px; }
  .confirm-grid > div { display: flex; gap: 10px; justify-content: space-between; align-items: baseline; flex-wrap: wrap; }
  .cg-l { color: var(--kc-sub); font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; }
  .cg-v { color: var(--kc-text); font-weight: 600; text-align: right; max-width: 60%; word-break: break-word; }
  .public-success-btn { display: inline-block; background: transparent; border: 1.5px solid var(--kc-accent); color: var(--kc-accent); border-radius: 10px; padding: 10px 18px; text-decoration: none; font-weight: 700; text-transform: uppercase; letter-spacing: 0.10em; font-size: 12px; margin-top: 14px; }
`;
